// PoliticaPrivacidadToppings.jsx
import React, { useEffect } from "react";
import { Container, Typography, Divider, Box, Link } from "@mui/material";
import logo from "../../Recursos/logo.svg"; // tu logo de Toppings

export default function PoliticaPrivacidad() {
    useEffect(() => {
        // SEO: title
        document.title = "Política de Privacidad - Toppings Colón";

        // SEO: meta description
        const meta = document.querySelector('meta[name="description"]');
        const description = "Política de privacidad de Toppings Colón: cómo recogemos, usamos y protegemos los datos de contacto (nombre, correo, teléfono) de clientes en Colón, Colombia.";
        if (meta) {
            meta.setAttribute("content", description);
        } else {
            const m = document.createElement("meta");
            m.name = "description";
            m.content = description;
            document.head.appendChild(m);
        }

        // JSON-LD (Organization + ContactPoint)
        const jsonLd = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Toppings Colón",
            "url": "https://toppings-colon.web.app",
            "contactPoint": [{
                "@type": "ContactPoint",
                "email": "info@toppings-colon.web.app",
                "contactType": "customer service",
                "areaServed": "CO"
            }],
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Colón",
                "addressCountry": "Colombia"
            }
        };
        const scriptId = "jsonld-politica-privacidad";
        let script = document.getElementById(scriptId);
        if (script) script.remove();
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        script.text = JSON.stringify(jsonLd);
        document.head.appendChild(script);

        return () => {
            const s = document.getElementById(scriptId);
            if (s) s.remove();
        };
    }, []);

    return (
        <Container maxWidth="md" sx={{ py: 6, pt:16 }}>
            {/* Logo */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                    component="img"
                    src={logo}
                    alt="Logo Toppings Colón"
                    sx={{ width: 150, height: "auto" }}
                />
            </Box>

            <Typography variant="h4" component="h1" gutterBottom>
                Política de Privacidad
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                Toppings Colón — Comida rápida, helados y bebidas (Colón, Colombia)
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">1. Introducción</Typography>
                <Typography paragraph>
                    En <strong>Toppings Colón</strong> (https://toppings-colon.web.app) respetamos tu privacidad y nos comprometemos a proteger tus datos personales.
                    Esta política explica qué datos recopilamos, con qué fines, cómo los tratamos y los derechos que te asisten.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">2. Responsable del tratamiento</Typography>
                <Typography paragraph>
                    Responsable: <strong>Toppings Colón</strong>.<br />
                    Sitio web: <Link href="https://toppings-colon.web.app">https://toppings-colon.web.app</Link><br />
                    Correo de contacto: <Link href="mailto:info@toppings-colon.web.app">info@toppings-colon.web.app</Link><br />
                    Ubicación: Colón, Colombia.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">3. Datos que recopilamos</Typography>
                <Typography paragraph>
                    Recopilamos datos que tú nos facilitas voluntariamente a través del formulario de contacto o pedidos online:
                </Typography>
                <ul>
                    <li>Nombre</li>
                    <li>Correo electrónico</li>
                    <li>Teléfono</li>
                    <li>Detalles del pedido o consulta</li>
                </ul>
                <Typography paragraph>
                    No recopilamos datos de pago ni gestionamos ventas fuera de la plataforma de pedidos de Toppings Colón.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">4. Finalidades del tratamiento</Typography>
                <Typography paragraph>
                    Usamos tus datos para:
                </Typography>
                <ol>
                    <li>Responder a tus consultas o solicitudes de pedidos.</li>
                    <li>Enviar información relevante sobre promociones, pedidos y novedades.</li>
                    <li>Mejorar la calidad del servicio y la comunicación con nuestros clientes.</li>
                </ol>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">5. Legitimación</Typography>
                <Typography paragraph>
                    La base legal para el tratamiento es el consentimiento que otorgas al enviar formularios o realizar pedidos, y el interés legítimo de Toppings Colón para gestionar la relación con sus clientes.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">6. Conservación de los datos</Typography>
                <Typography paragraph>
                    Conservaremos tus datos mientras sea necesario para cumplir la finalidad para la que se recabaron y cumplir obligaciones legales. Si el tratamiento se basa en el consentimiento, los datos se conservarán hasta que lo retires o solicites su supresión.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">7. Destinatarios</Typography>
                <Typography paragraph>
                    No compartimos tus datos con terceros salvo para prestar el servicio (proveedores técnicos que soportan formularios o pedidos) o cuando exista obligación legal.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">8. Seguridad</Typography>
                <Typography paragraph>
                    Implementamos medidas razonables para proteger tus datos contra accesos no autorizados o pérdida. No obstante, ningún sistema es 100% seguro.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">9. Derechos del interesado</Typography>
                <Typography paragraph>
                    Puedes solicitar:
                </Typography>
                <ol>
                    <li>Acceso a tus datos.</li>
                    <li>Rectificación de datos inexactos.</li>
                    <li>Supresión (derecho al olvido) cuando proceda.</li>
                    <li>Limitación del tratamiento.</li>
                    <li>Portabilidad de los datos (cuando proceda).</li>
                    <li>Oposición al tratamiento por motivos legítimos.</li>
                </ol>
                <Typography paragraph>
                    Para ejercer tus derechos o presentar consultas, escríbenos a <Link href="mailto:info@toppings-colon.web.app">info@toppings-colon.web.app</Link>.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">10. Menores</Typography>
                <Typography paragraph>
                    No solicitamos ni recopilamos conscientemente datos de menores de edad. Si detectamos que se han facilitado datos de menores sin consentimiento parental, los eliminaremos cuando tengamos conocimiento.
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 3 }}>
                <Typography variant="h6">11. Cambios en la política</Typography>
                <Typography paragraph>
                    Nos reservamos el derecho a actualizar esta política. Cuando hagamos cambios significativos, lo comunicaremos en el sitio web con la fecha de la última revisión.
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary" paragraph>
                Última actualización: {new Date().toLocaleDateString()}.
            </Typography>

            <Box sx={{ mt: 4, p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
                <Typography variant="subtitle2">Aviso:</Typography>
                <Typography variant="body2">
                    Este texto es informativo y se adapta a los datos proporcionados. No sustituye asesoramiento legal profesional. Para condiciones legales específicas, consulta con un abogado.
                </Typography>
            </Box>
        </Container>
    );
}
